import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { resources } from '@gg/shared/i18n';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@gg/shared';

const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('gg_locale') : null;
const browser = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2) : DEFAULT_LOCALE;
const initial = (SUPPORTED_LOCALES as readonly string[]).includes(stored ?? browser)
  ? (stored ?? browser)
  : DEFAULT_LOCALE;

void i18n.use(initReactI18next).init({
  resources,
  lng: initial,
  fallbackLng: DEFAULT_LOCALE,
  interpolation: { escapeValue: false },
});

if (typeof document !== 'undefined') document.documentElement.lang = initial;

export function setLocale(l: string): void {
  localStorage.setItem('gg_locale', l);
  void i18n.changeLanguage(l);
  document.documentElement.lang = l;
}

/** EUR amount from cents; whole euros without decimals ("55 €"), as in the design. */
export function formatPrice(cents: number, locale = i18n.language): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/** EUR amount from cents, always with two decimals ("4,25 €"): the insurer's price format. */
export function formatPriceExact(cents: number, locale = i18n.language): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

/**
 * Euro amount typed by an organiser ("9", "9,50", "9.5") → cents. Empty → null; anything
 * else → NaN, which the shared Zod schemas reject.
 */
export function parseEurosToCents(input: string): number | null {
  const s = input.trim().replace(/\s|€/g, '').replace(',', '.');
  if (s === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return Number.NaN;
  return Math.round(Number(s) * 100);
}

/** Cents → the organiser's input format ("9", "9,50" in French); null → "". */
export function centsToEuroInput(cents: number | null, locale = i18n.language): string {
  if (cents === null) return '';
  const s = cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
  return locale === 'fr' ? s.replace('.', ',') : s;
}

export default i18n;
